import React from 'react';
import {
  Box, Paper, TextField, Button, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, Typography, Table, TableHead, TableRow,
  TableCell, TableBody, TablePagination, Stack, Divider, Tooltip, Chip,
  TableContainer, CircularProgress, Snackbar, Alert, Checkbox
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { Add, Delete, Edit, Refresh, RestartAlt, Close, CleaningServices } from '@mui/icons-material';

import Client from '../Client';
import Config from '../ClientConfig';

// Client-side PDF
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

/** Minimal top bar for the full-screen modal */
function AppBarLike({ title, onClose, onCsv, onPdf }) {
  return (
    <Box sx={{
      px: 2, py: 1, bgcolor: '#273442', color: '#fff',
      display: 'flex', alignItems: 'center', gap: 1
    }}>
      <IconButton size="small" onClick={onClose} sx={{ color: 'inherit' }}>
        <Close />
      </IconButton>
      <Typography variant="h6" sx={{ flexGrow: 1 }}>{title}</Typography>
      <Button color="inherit" onClick={onCsv}>CSV</Button>
      <Button color="inherit" onClick={onPdf}>PDF</Button>
    </Box>
  );
}

const stripDiacritics = (text = "") => {
  return text
    .normalize("NFD")                 // separate accent from letter
    .replace(/[\u0300-\u036f]/g, "")  // remove all accents
    .replace(/ș/g, "s")
    .replace(/Ș/g, "S")
    .replace(/ț/g, "t")
    .replace(/Ț/g, "T");
};

class ProductRecipes extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      client: new Client('none'),

      // data
      recipes: [],
      filtered: [],
      selected: null,

      // lookups
      products: [],
      productNameMap: {},
      finishedOptions: [],
      ingredientOptions: [],

      // filters
      search: '',
      searching: false,
      searchTimer: null,

      // pagination
      page: 0,
      rowsPerPage: 10,

      // create/edit modal
      modalOpen: false,
      isEdit: false,
      form: {
        iref: '',
        description: '',
        productIref: '',
        ingredients: [{ productIref: '', quantity: 0, notes: '' }]
      },
      formErrors: {},

      loading: false,
      saving: false,

      // dialogs
      errorDialog: { open: false, detail: '' },
      toastOpen: false,
      toastMsg: '',
      toastSeverity: 'success',

      // Cost simulator
      simOpen: false,
      simMonth: '',
      simLoading: false,
      simError: '',
      simWarning: '',
      simProductName: '',
      simFinishedIref: '',
      simSourceLabel: 'current',
      simSourceRecipe: [],
      simRecipe: [],
      simSnapshot: {},
      simNewProduct: '',
      simPrice: '',
      simVat: '',

      // guards
      simFetching: false,
      simExporting: false,
    };
  }

  componentDidMount() {
    this._unmounted = false;
    this.loadLookupsAndList();
  }

  componentWillUnmount() {
    this._unmounted = true;
    if (this.state.searchTimer) clearTimeout(this.state.searchTimer);
  }

  // ---- helpers ----
  openError = (detail) => this.setState({ errorDialog: { open: true, detail: detail || 'Error' } });
  closeError = () => this.setState({ errorDialog: { open: false, detail: '' } });

  showToast = (msg, severity = 'success') => this.setState({ toastOpen: true, toastMsg: msg, toastSeverity: severity });
  closeToast = () => this.setState({ toastOpen: false, toastMsg: '' });

  getProductName = (id) => {
    if (!id) return '';
    const key = String(id);
    const { productNameMap, ingredientOptions, finishedOptions } = this.state;
    if (productNameMap[key]) return productNameMap[key];
    const opt = (ingredientOptions || []).find(o => o.value === key) || (finishedOptions || []).find(o => o.value === key);
    return (opt && (opt.label || opt.value)) || key;
  };

  // Common sorter by ingredient product name
  sortIngredientsByName = (lines = []) =>
    lines.slice().sort((a, b) =>
      (this.getProductName(a.product) || '').localeCompare(
        this.getProductName(b.product) || '',
        undefined,
        { sensitivity: 'base', numeric: true }
      )
    );

  prettifyError = (msg) => {
    const { productNameMap } = this.state;
    return String(msg || '').replace(/'([0-9a-zA-Z_-]{6,36})'/g, (_m, id) => `'${productNameMap[String(id)] || id}'`);
  };

  mergeRecipeIntoState = (updated) => {
    this.setState(prev => {
      const recipes = (prev.recipes || []).map(r => r.iref === updated.iref ? updated : r);
      const filtered = this.applySearchFilter(recipes, prev.search, prev.productNameMap);
      const keepSelected = prev.selected && prev.selected.iref === updated.iref ? updated : prev.selected;
      return { recipes, filtered, selected: keepSelected, modalOpen: false, saving: false };
    });
  };

  fetchRecipeAndMerge = (iref) => {
    if (!iref) return;
    try {
      const res = this.state.client.getData(`${Config.API_URL_PRODUCT_RECIPE}${iref}/`, {}) || {};
      const fresh = res.productRecipe || res;
      if (!fresh || !fresh.iref) return;
      this.setState(prev => {
        const recipes = (prev.recipes || []).map(r => r.iref === fresh.iref ? fresh : r);
        const filtered = this.applySearchFilter(recipes, prev.search, prev.productNameMap);
        const selected = prev.selected && prev.selected.iref === fresh.iref ? fresh : prev.selected;
        return { recipes, filtered, selected };
      });
    } catch (e) { this.openError(this.prettifyError(String(e))); }
  };

  // ---- data load ----
  loadLookupsAndList = () => {
    this.setState({ loading: true }, () => {
      try {
        const prodResp = this.state.client.getData(Config.API_URL_PRODUCT + 'list_products/', { status: 2, orderBy_name: true }) || {};
        const prods = Array.isArray(prodResp.products) ? prodResp.products : (Array.isArray(prodResp) ? prodResp : []);
        const productNameMap = {};
        prods.forEach(p => {
          const pid = p.iref || p.id || p.uid || p.code;
          const name = p.name || p.productName || p.description || p.code || String(pid);
          if (pid) productNameMap[String(pid)] = String(name);
        });
        const finishedOptions = prods
          .filter(p => Number(p.status) === 2 && (p.isPurchased === false || Number(p.isPurchased) === 0))
          .map(p => ({ value: String(p.iref || p.id || p.uid || p.code), label: productNameMap[String(p.iref || p.id || p.uid || p.code)] }));
        const ingredientOptions = prods
          .filter(p => Number(p.status) === 2 && (p.isPurchased === true || Number(p.isPurchased) === 1))
          .map(p => ({ value: String(p.iref || p.id || p.uid || p.code), label: productNameMap[String(p.iref || p.id || p.uid || p.code)] }));

        const listResp = this.state.client.getData(Config.API_URL_PRODUCT_RECIPE, {}) || {};
        const recipes = Array.isArray(listResp.productRecipe) ? listResp.productRecipe : (Array.isArray(listResp) ? listResp : []);
        const filtered = this.applySearchFilter(recipes, this.state.search, productNameMap);

        const selectedIref = this.state.selected?.iref;
        const selected = selectedIref ? recipes.find(r => r.iref === selectedIref) || null : this.state.selected;

        this.setState({ products: prods, productNameMap, finishedOptions, ingredientOptions, recipes, filtered, selected, loading: false, page: 0 });
      } catch (e) {
        this.setState({ loading: false });
        this.openError(this.prettifyError(String(e)));
      }
    });
  };

  refreshList = () => this.loadLookupsAndList();

  // ---- filters ----
  applySearchFilter = (recipes, search, nameMap) => {
    if (!search) return recipes.slice();
    const term = search.toLowerCase();
    return recipes.filter(r => {
      const pname = (r.product && (r.product.name || r.product.description)) || nameMap[String(r.product?.iref)] || '';
      return String(pname).toLowerCase().includes(term);
    });
  };

  onSearchChange = (e) => {
    const value = e.target.value || '';
    if (this.state.searchTimer) clearTimeout(this.state.searchTimer);
    this.setState({ search: value, searching: true });
    const timer = setTimeout(() => {
      const filtered = this.applySearchFilter(this.state.recipes, value, this.state.productNameMap);
      if (!this._unmounted) this.setState({ filtered, page: 0, searching: false, searchTimer: null });
    }, 180);
    this.setState({ searchTimer: timer });
  };

  // ---- selection ----
  onSelectRecipe = (rec) => this.setState({ selected: rec });

  // ---- create/edit modal ----
  openNewModal = () => {
    this.setState({
      modalOpen: true,
      isEdit: false,
      form: { iref: '', description: '', productIref: '', ingredients: [{ productIref: '', quantity: 0, notes: '' }] },
      formErrors: {}
    });
  };

  openEditModal = () => {
    const r = this.state.selected;
    if (!r) return;

    // Sort recipe lines by ingredient name for the edit modal too
    const sortedLines = Array.isArray(r.recipeItem_recipe)
      ? this.sortIngredientsByName(r.recipeItem_recipe)
      : [];

    const ingredients = sortedLines.length
      ? sortedLines.map(ln => ({ productIref: String(ln.product), quantity: Number(ln.quantity || 0), notes: ln.notes || '' }))
      : [{ productIref: '', quantity: 0, notes: '' }];

    this.setState({
      modalOpen: true,
      isEdit: true,
      form: { iref: String(r.iref), description: r.description || '', productIref: String(r.product?.iref || ''), ingredients },
      formErrors: {}
    });
  };

  closeModal = () => this.setState({ modalOpen: false });

  setFormField = (field, value) => this.setState(prev => ({ form: { ...prev.form, [field]: value } }), this.validateForm);

  addIngredientRow = () => this.setState(prev => ({ form: { ...prev.form, ingredients: [...prev.form.ingredients, { productIref: '', quantity: 0, notes: '' }] } }), this.validateForm);

  removeIngredientRow = (idx) => {
    this.setState(prev => {
      const next = prev.form.ingredients.slice();
      next.splice(idx, 1);
      if (next.length === 0) next.push({ productIref: '', quantity: 0, notes: '' });
      return { form: { ...prev.form, ingredients: next } };
    }, this.validateForm);
  };

  updateIngredient = (idx, patch) => this.setState(prev => ({ form: { ...prev.form, ingredients: prev.form.ingredients.map((ln, i) => i === idx ? { ...ln, ...patch } : ln) } }), this.validateForm);

  validateForm = () => {
    const { form } = this.state;
    const errors = {};
    if (!form.description || !String(form.description).trim()) errors.description = 'Description is required.';
    if (!form.productIref) errors.productIref = 'Select finished product.';
    if (!form.ingredients || form.ingredients.length === 0) {
      errors.ingredients = 'Add at least one ingredient.';
    } else {
      const hasAnyIngredient = form.ingredients.some(ln => ln.productIref && String(ln.productIref).trim());
      if (!hasAnyIngredient) errors.ingredients = 'Select at least one ingredient product.';
      const seen = new Set();
      form.ingredients.forEach((ln, i) => {
        const rowErr = {};
        // if (!ln.productIref || !String(ln.productIref).trim()) rowErr.product = 'Select product';
        // if (ln.quantity == null || isNaN(Number(ln.quantity))) rowErr.quantity = 'Number required';
        if (Number(ln.quantity) < 0) rowErr.quantity = 'Must be ≥ 0';
        if (String(ln.productIref) === String(form.productIref)) rowErr.product = 'Same as finished product';
        const dupKey = ln.productIref ? String(ln.productIref) : '';
        if (dupKey) {
          if (seen.has(dupKey)) rowErr.product = 'Duplicate ingredient';
          seen.add(dupKey);
        }
        if (Object.keys(rowErr).length) {
          if (!errors.rows) errors.rows = {};
          errors.rows[i] = rowErr;
        }
      });
    }
    this.setState({ formErrors: errors });
    return Object.keys(errors).length === 0;
  };

  buildFormPayload = () => {
    const { description, productIref, ingredients } = this.state.form;
    const payload = { description: description || '', product: productIref || '' };
    (ingredients || []).forEach((ln, i) => {
      payload[`recipeItem_recipe[${i}][product]`] = ln.productIref || '';
      payload[`recipeItem_recipe[${i}][quantity]`] = ln.quantity ?? 0;
      payload[`recipeItem_recipe[${i}][notes]`] = ln.notes || '';
    });
    return payload;
  };

  // SAVE using synchronous pushData, but allow React to paint first
  onSave = () => {
    if (!this.validateForm() || this.state.saving) return;
    const { isEdit, form } = this.state;
    const payload = this.buildFormPayload();
    const url = isEdit ? `${Config.API_URL_PRODUCT_RECIPE}${form.iref}/edit/` : `${Config.API_URL_PRODUCT_RECIPE}`;

    this.setState({ saving: true }, () => {
      // Yield once so the UI shows "Saving…" before the blocking call.
      setTimeout(() => {
        try {
          const res = this.state.client.pushData(url, payload); // synchronous
          if (res && res.error) throw new Error(res.detail || res.error);
          const updated = res.productRecipe ? res.productRecipe : res;

          if (isEdit) {
            if (!this._unmounted) this.mergeRecipeIntoState(updated);
            this.fetchRecipeAndMerge(updated.iref);
            if (!this._unmounted) this.setState({ saving: false });
            this.showToast('Recipe updated');
          } else {
            if (!this._unmounted) {
              this.setState(prev => {
                const recipes = [updated, ...(prev.recipes || [])];
                const filtered = this.applySearchFilter(recipes, prev.search, prev.productNameMap);
                return { recipes, filtered, selected: updated, modalOpen: false, saving: false, page: 0 };
              }, () => this.fetchRecipeAndMerge(updated.iref));
            }
            this.showToast('Recipe created');
          }
        } catch (e) {
          if (!this._unmounted) this.setState({ saving: false });
          this.openError(this.prettifyError(String(e?.message || e)));
        }
      }, 0);
    });
  };

  // ----- CSV export helpers (Excel-friendly) -----
  csvEscape = (v) => {
    if (v === null || v === undefined) return '';
    const s = String(v);
    if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };

  downloadTextFile = (filename, text, mime = 'text/csv;charset=utf-8;') => {
    // Explicit UTF-8 BOM bytes make Excel detect diacritics reliably.
    const utf8Bom = new Uint8Array([0xEF, 0xBB, 0xBF]);
    const blob = new Blob([utf8Bom, text], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  };

  exportRecipesWithIngredientsToCsv = () => {
    try {
      const recipes = Array.isArray(this.state.filtered) ? this.state.filtered : [];
      if (!recipes.length) {
        this.showToast('No recipes to export.', 'info');
        return;
      }

      const headers = [
        'recipeIref',
        'finishedProductIref',
        'finishedProduct',
        'recipeDescription',
        'ingredientProductIref',
        'ingredientProduct',
        'quantity',
        'notes',
      ];

      const delim = ',';                 // Excel friendly
      const lines = ['sep=,', headers.join(delim)];  // force delimiter in Excel

      // optional: stable sorting
      const sortedRecipes = recipes.slice().sort((a, b) => {
        const an = ((a.product && (a.product.name || a.product.description)) || this.getProductName(a.product?.iref) || '').toLowerCase();
        const bn = ((b.product && (b.product.name || b.product.description)) || this.getProductName(b.product?.iref) || '').toLowerCase();
        return an.localeCompare(bn, undefined, { sensitivity: 'base' });
      });

      sortedRecipes.forEach((r) => {
        const finishedIref = String(r.product?.iref || '');
        const finishedName =
          (r.product && (r.product.name || r.product.description)) ||
          this.getProductName(r.product?.iref) ||
          finishedIref;

        const recipeDesc = r.description ?? '';

        // ingredient lines (sorted like your UI)
        const ing = Array.isArray(r.recipeItem_recipe) ? this.sortIngredientsByName(r.recipeItem_recipe) : [];

        // If a recipe has no lines, still export 1 row (empty ingredient columns)
        if (!ing.length) {
          const row = [
            r.iref ?? '',
            finishedIref,
            finishedName,
            recipeDesc,
            '',
            '',
            '',
            '',
          ].map(this.csvEscape);
          lines.push(row.join(delim));
          return;
        }

        ing.forEach((ln) => {
          const ingIref = String(ln.product || '');
          const ingName = this.getProductName(ingIref) || ingIref;

          const row = [
            r.iref ?? '',
            finishedIref,
            finishedName,
            recipeDesc,
            ingIref,
            ingName,
            ln.quantity ?? '',
            ln.notes ?? '',
          ].map(this.csvEscape);

          lines.push(row.join(delim));
        });
      });

      const d = new Date();
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');

      this.downloadTextFile(`recipes_with_ingredients_${yyyy}-${mm}-${dd}.csv`, lines.join('\r\n'));
      this.showToast(`Exported ${lines.length - 2} line(s).`, 'success');
    } catch (e) {
      this.showToast(`Export failed: ${e?.message || e}`, 'error');
    }
  };

  // ---- Cost Simulator helpers ----
  numberOr = (v, fb = 0) => {
    const n = typeof v === 'number' ? v : parseFloat(String(v ?? '').replace(',', '.'));
    return Number.isFinite(n) ? n : fb;
  };

  normalizeRecipeLines = (rec) => {
    const lines = Array.isArray(rec?.recipeItem_recipe) ? rec.recipeItem_recipe : [];
    return lines.map(ln => {
      const iref = String(ln.product || '');
      const name = this.getProductName(iref) || iref;
      return { iref, name, qty: Number(ln.quantity || 0), useSnapshot: true, price: '' };
    });
  };

  computeCost = () => {
    const { simRecipe, simSnapshot } = this.state;
    const bag = {};
    simRecipe.forEach(l => {
      const key = String(l.iref || '').trim();
      if (!key) return;
      const prev = bag[key] || { iref: key, name: l.name || key, qty: 0, unitPrice: NaN };
      const qty = this.numberOr(l.qty, 0);
      const snap = simSnapshot[key];
      const unitFromSnap = l.useSnapshot && Number.isFinite(snap) ? snap : NaN;
      const manual = this.numberOr(l.price, NaN);
      const unitPrice = Number.isFinite(unitFromSnap) ? unitFromSnap : manual;
      bag[key] = { iref: key, name: prev.name, qty: prev.qty + qty, unitPrice: Number.isFinite(unitPrice) ? unitPrice : prev.unitPrice };
    });
    const lines = Object.values(bag).map(row => ({ ...row, lineCost: Number.isFinite(row.unitPrice) ? row.qty * row.unitPrice : NaN }));
    const total = lines.reduce((s, l) => s + (Number.isFinite(l.lineCost) ? l.lineCost : 0), 0);
    return { lines, total };
  };

  computePriceAndMargin = (total) => {
    // total = total cost from computeCost()
    const priceWithVat = this.numberOr(this.state.simPrice, NaN);
    const vatPct = this.numberOr(this.state.simVat, NaN);

    let priceNoVat = NaN;
    let margin = NaN;
    let marginPct = NaN;

    if (Number.isFinite(priceWithVat)) {
      const factor = Number.isFinite(vatPct) ? (1 + vatPct / 100) : 1;
      priceNoVat = priceWithVat / factor;
      margin = priceNoVat - total;
      if (Number.isFinite(priceNoVat) && priceNoVat !== 0) {
        marginPct = (margin / priceNoVat) * 100;
      }
    }

    return { priceWithVat, vatPct, priceNoVat, margin, marginPct };
  };

  toSnapshotMap = (payload) => {
    const arr = Array.isArray(payload) ? payload : (payload?.items || []);
    const map = {};
    arr.forEach(({ iref, price }) => {
      if (!iref) return;
      const p = Number(price);
      if (Number.isFinite(p)) map[String(iref)] = p;
    });
    return map;
  };

  // ---- Cost Simulator actions ----
  openCostSimulator = () => {
    const r = this.state.selected;
    const d = new Date();
    const monthDefault = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const src = r ? this.normalizeRecipeLines(r) : [];
    this.setState({
      simOpen: true,
      simMonth: monthDefault,
      simProductName: (r?.product?.name || this.getProductName(r?.product?.iref) || ''),
      simFinishedIref: r?.product?.iref ? String(r.product.iref) : '',
      simSourceLabel: r ? 'current' : 'empty',
      simSourceRecipe: src,
      simRecipe: src,
      simSnapshot: {},
      simError: '',
      simNewProduct: '',
      simPrice: '',
      simVat: '',
    });
  };

  closeCostSimulator = () => this.setState({ simOpen: false });

  simStartEmpty = () => this.setState({ simSourceLabel: 'empty', simSourceRecipe: [], simRecipe: [] });
  simResetToSource = () => this.setState(prev => ({ simRecipe: prev.simSourceRecipe.slice() }));
  simAddRow = () => this.setState(prev => ({ simRecipe: [...prev.simRecipe, { iref: '', name: '', qty: 1, useSnapshot: true, price: '' }] }));
  simClearRows = () => this.setState({ simRecipe: [] });
  simRemoveRow = (idx) => this.setState(prev => ({ simRecipe: prev.simRecipe.filter((_, i) => i !== idx) }));
  simUpdateRow = (idx, patch) => this.setState(prev => ({ simRecipe: prev.simRecipe.map((r, i) => (i === idx ? { ...r, ...patch } : r)) }));

  simFetchSnapshot = () => {
    if (this.state.simFetching) return;
    const { simMonth } = this.state;
    this.setState({ simFetching: true, simLoading: true, simError: '', simWarning: '' }, () => {
      try {
        const url = `${Config.API_URL_REPORTS_MONTH_CLOSE}month-close-prices/`;
        const params = { month: simMonth || undefined, ptype: 'ingredients' };
        const data = this.state.client.getData(url, params) || {};
        const map = this.toSnapshotMap(data);
        const warning = data?.meta?.warning || '';
        this.setState({ simSnapshot: map, simLoading: false, simFetching: false, simWarning: warning });
      } catch (e) {
        this.setState({ simLoading: false, simFetching: false, simError: String(e), simWarning: '' });
      }
    });
  };

  hasAnySelectedIngredient = () => (this.state.simRecipe || []).some(r => r.iref && String(r.iref).trim());
  canExport = () => !!(this.state.simFinishedIref && String(this.state.simFinishedIref).trim()) && this.hasAnySelectedIngredient();

  buildPdfPayload = () => {
    const { lines, total } = this.computeCost();
    const { simProductName, simFinishedIref, simMonth, simSourceLabel, simSnapshot, simNewProduct } = this.state;
    const { priceWithVat, vatPct, priceNoVat, margin, marginPct } = this.computePriceAndMargin(total);
    return {
      finished: {
        iref: simFinishedIref,
        name: (simNewProduct && simNewProduct.trim()) || simProductName || this.getProductName(simFinishedIref) || simFinishedIref,
      },
      month: simMonth || '',
      source: simSourceLabel,
      snapshot_count: Object.keys(simSnapshot || {}).length,
      ingredients: lines.map(l => ({
        iref: l.iref, name: l.name, quantity: this.numberOr(l.qty),
        unit_price: Number.isFinite(l.unitPrice) ? l.unitPrice : null,
        total: Number.isFinite(l.lineCost) ? l.lineCost : null
      })),
      total_cost: total,
      price_with_vat: Number.isFinite(priceWithVat) ? priceWithVat : null,
      vat_pct: Number.isFinite(vatPct) ? vatPct : null,
      price_without_vat: Number.isFinite(priceNoVat) ? priceNoVat : null,
      margin: Number.isFinite(margin) ? margin : null,
      margin_pct: Number.isFinite(marginPct) ? marginPct : null,
    };
  };

  simExportCsv = () => {
    if (!this.canExport()) {
      this.showToast('Select a finished product and add at least one ingredient.', 'warning');
      return;
    }
    const { lines, total } = this.computeCost();
    const finishedName =
      (this.state.simNewProduct && this.state.simNewProduct.trim()) ||
      this.state.simProductName ||
      this.getProductName(this.state.simFinishedIref) ||
      this.state.simFinishedIref ||
      'cost-simulation';
    const { priceWithVat, vatPct, priceNoVat, margin, marginPct } =
      this.computePriceAndMargin(total);
    const rows = [
      ['finished_product', finishedName],
      ['month', this.state.simMonth || '(latest)'],
      ['source', this.state.simSourceLabel || ''],
      ['snapshot_prices', String(Object.keys(this.state.simSnapshot || {}).length)],
      [],
      ['ingredient_name', 'qty', 'unit_price', 'line_cost'],
      ...lines.map(l => [
        l.name,
        Number.isFinite(l.qty) ? l.qty.toFixed(3) : '',
        Number.isFinite(l.unitPrice) ? l.unitPrice.toFixed(4) : '',
        Number.isFinite(l.lineCost) ? l.lineCost.toFixed(4) : '',
      ]),
      [],
    ];
    rows.push(
      ['Total cost', '', '', total.toFixed(4)],
    );
    if (Number.isFinite(priceNoVat)) {
      rows.push(['Price without VAT', '', '', priceNoVat.toFixed(4)]);
    }
    if (Number.isFinite(margin)) {
      rows.push(['Margin', '', '', margin.toFixed(4)]);
      if (Number.isFinite(marginPct)) {
        rows.push(['Margin %', '', '', marginPct.toFixed(2)]);
      }
    }
    const csv = rows
      .map(r => r.map(x => `"${String(x).replace(/"/g, '""')}"`).join(','))
      .join('\r\n');
    this.downloadTextFile(
      `${finishedName.replace(/\s+/g, '-').toLowerCase()}-${this.state.simMonth || 'latest'}.csv`,
      csv
    );
  };

  simExportPdf = () => {
    if (this.state.simExporting) return;
    if (!this.canExport()) { this.showToast('Select a finished product and add at least one ingredient.', 'warning'); return; }
    this.setState({ simExporting: true }, () => {
      try {
        const payload = this.buildPdfPayload();
        const { lines, total } = this.computeCost();

        const doc = new jsPDF({ unit: 'pt', format: 'a4' });
        const docMargin = 40;
        const pw = doc.internal.pageSize.getWidth();
        const ph = doc.internal.pageSize.getHeight();

        doc.setFont('helvetica', 'bold'); doc.setFontSize(16);
        doc.text('Cost Simulation', docMargin, 50);

        doc.setFont('helvetica', 'normal'); doc.setFontSize(10);
        const meta = [
          `Finished product: ${stripDiacritics(payload.finished?.name) || payload.finished?.iref || ''}`,
          `Month: ${payload.month || '(latest)'}`,
          `Source: ${payload.source || ''}`,
          `Snapshot prices: ${payload.snapshot_count || 0}`,
          `Generated: ${new Date().toLocaleString()}`
        ];
        let y = 70; meta.forEach(t => { doc.text(t, docMargin, y); y += 14; });

        autoTable(doc, {
          head: [['Ingredient', 'Qty', 'Unit price', 'Line cost']],
          body: lines.map(l => [
            stripDiacritics(l.name),
            Number.isFinite(l.qty) ? l.qty.toFixed(3) : '',
            Number.isFinite(l.unitPrice) ? l.unitPrice.toFixed(4) : 'n/a',
            Number.isFinite(l.lineCost) ? l.lineCost.toFixed(4) : 'n/a',
          ]),
          startY: y + 8,
          margin: { left: docMargin, right: docMargin },
          styles: { font: 'helvetica', fontSize: 10, cellPadding: 6, overflow: 'linebreak' },
          columnStyles: {
            0: { halign: 'left' },   // Ingredient
            1: { halign: 'right' },  // Qty
            2: { halign: 'right' },  // Unit price
            3: { halign: 'right' },  // Line cost
          },
          // Ensure the HEADER follows the same alignment
          didParseCell: (data) => {
            if (data.section === 'head') {
              // column.index is 0-based for array-based head
              if (data.column.index === 0) data.cell.styles.halign = 'left';
              else data.cell.styles.halign = 'right';
            }
          },
          headStyles: { fontStyle: 'bold' },
          didDrawPage: () => {
            doc.setFontSize(9);
            doc.text(`Page ${doc.internal.getNumberOfPages()}`, pw - docMargin, ph - 20, { align: 'right' });
          },
        });

        const fy = doc.lastAutoTable?.finalY || y + 40;
        const { priceWithVat, vatPct, priceNoVat, margin, marginPct } = this.computePriceAndMargin(total);
        const rightX = pw - docMargin;
        const labelX = pw - docMargin - 200;
        let lineY = fy + 24;

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        doc.text('Total cost:', labelX, lineY, { align: 'right' });
        doc.text(total.toFixed(4), rightX, lineY, { align: 'right' });
        doc.setFont('helvetica', 'normal');
        if (Number.isFinite(priceNoVat)) {
          lineY += 16;
          doc.text('Price without VAT:', labelX, lineY, { align: 'right' });
          doc.text(priceNoVat.toFixed(4), rightX, lineY, { align: 'right' });
        }
        if (Number.isFinite(margin)) {
          lineY += 16;
          doc.text('Margin:', labelX, lineY, { align: 'right' });
          const color = margin >= 0 ? [30, 120, 30] : [200, 40, 40]; // green/red
          doc.setTextColor(...color);
          doc.text(margin.toFixed(4), rightX, lineY, { align: 'right' });
          doc.setTextColor(0, 0, 0);
          if (Number.isFinite(marginPct)) {
            doc.setFontSize(9);
            doc.text(`(${marginPct.toFixed(2)}%)`, rightX, lineY + 12, { align: 'right' });
            doc.setFontSize(11);
          }
        }
        const fnameBase = (payload.finished?.name || 'cost-simulation').replace(/\s+/g, '-').toLowerCase();
        doc.save(`${fnameBase}-${payload.month || 'latest'}.pdf`);
        this.showToast('PDF exported.', 'success');
      } catch (e) {
        this.openError(this.prettifyError(String(e?.message || e)));
      } finally {
        this.setState({ simExporting: false });
      }
    });
  };

  // ---- UI ----
  renderToolbar() {
    const { searching } = this.state;
    return (
      <Paper
        elevation={1}
        sx={{
          flexShrink: 0,
          p: { xs: 0.5, sm: 0.75 },
          mb: 0.75,
          borderRadius: '14px',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 0.75,
            alignItems: 'center',
            flexWrap: 'wrap',
            '& .MuiButton-root': {
              height: 32,
              borderRadius: '11px',
              textTransform: 'none',
              fontWeight: 700,
            },
          }}
        >
          <TextField
            label="Search finished product"
            value={this.state.search}
            onChange={this.onSearchChange}
            size="small"
            margin="dense"
            sx={{
              minWidth: { xs: '100%', sm: 220 },
              flexGrow: 1,
              '& .MuiInputBase-root': {
                height: 34,
                borderRadius: '12px',
              },
              '& .MuiInputBase-input': {
                fontSize: {
                  xs: '16px',
                  sm: '0.84rem',
                },
              },
            }}
            InputProps={{ endAdornment: searching ? <CircularProgress size={16} /> : null }}
          />
          <Button size="small" variant="outlined" onClick={() => this.exportRecipesWithIngredientsToCsv('filtered')}>Export CSV</Button>
          <Button size="small" variant="contained" startIcon={<Add />} onClick={this.openNewModal}>New</Button>
          <Button size="small" variant="outlined" onClick={this.openCostSimulator}>Cost simulator</Button>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={this.refreshList}><Refresh fontSize="small" /></IconButton>
          </Tooltip>
          <Box sx={{ flexGrow: 1 }} />
          <Typography variant="body2" color="text.secondary">{this.state.filtered.length} recipes</Typography>
        </Box>
      </Paper>
    );
  }

  renderList() {
    const { filtered, page, rowsPerPage, selected, loading } = this.state;
    const start = page * rowsPerPage;
    const rows = rowsPerPage > 0 ? filtered.slice(start, start + rowsPerPage) : filtered;

    return (
      <Paper
        elevation={0}
        variant="outlined"
        sx={{
          p: 0,
          position: 'relative',
          height: '100%',
          minHeight: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: (theme) => theme.palette.mode === 'dark' ? 'action.selected' : 'grey.50',
          borderColor: 'divider',
          borderRadius: '14px',
        }}
      >
        <TableContainer
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'rgba(0,0,0,0.04)' }}>
                <TableCell sx={{ fontWeight: 700, width: 56 }}>Edit</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Finished product</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 700, width: 120 }} align="right">Ingredients</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(r => {
                const isSel = selected && selected.iref === r.iref;
                return (
                  <TableRow key={r.iref} hover selected={!!isSel} onClick={() => this.onSelectRecipe(r)} sx={{ cursor: 'pointer' }}>
                    <TableCell>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); this.setState({ selected: r }, this.openEditModal); }}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{(r.product && (r.product.name || r.product.description)) || this.getProductName(r.product?.iref)}</TableCell>
                    <TableCell>{r.description}</TableCell>
                    <TableCell align="right"><Chip size="small" label={Array.isArray(r.recipeItem_recipe) ? r.recipeItem_recipe.length : 0} /></TableCell>
                  </TableRow>
                );
              })}
              {!rows.length && !loading && (
                <TableRow><TableCell colSpan={4} align="center">No recipes</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={filtered.length}
          page={page}
          onPageChange={(_e, newPage) => this.setState({ page: newPage })}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => this.setState({ rowsPerPage: Number(e.target.value), page: 0 })}
          rowsPerPageOptions={[10, 25, { value: -1, label: 'All' }]}
          sx={{
            flexShrink: 0,
            borderTop: '1px solid rgba(15, 23, 42, 0.06)',
            minHeight: { xs: 38, sm: 50 },
            overflow: 'hidden',
            '& .MuiTablePagination-toolbar': {
              minHeight: { xs: 38, sm: 50 },
              px: { xs: 0.25, sm: 1 },
              flexWrap: 'nowrap',
            },
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              fontSize: { xs: '0.72rem', sm: '0.86rem' },
              m: 0,
            },
            '& .MuiIconButton-root': {
              p: { xs: 0.35, sm: 0.5 },
            },
          }}
        />

        {loading && (
          <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(255,255,255,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress size={28} />
          </Box>
        )}
      </Paper>
    );
  }

  renderDetails() {
    const r = this.state.selected;
    if (!r) return (
      <Paper
        elevation={1}
        sx={{
          p: 2,
          height: '100%',
          minHeight: 0,
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '14px',
        }}
      >
        <Typography variant="body2" color="text.secondary">Select a recipe to view details</Typography>
      </Paper>
    );

    // Use sorted ingredients for display
    const sortedForDisplay = this.sortIngredientsByName(r.recipeItem_recipe || []);

    return (
      <Paper
        elevation={1}
        sx={{
          p: { xs: 0.75, sm: 1.25 },
          height: '100%',
          minHeight: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '14px',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {(r.product && (r.product.name || r.product.description)) || this.getProductName(r.product?.iref)}
          </Typography>
          <Typography variant="body2" color="text.secondary">•</Typography>
          <Typography variant="body2" color="text.secondary">{r.description || '—'}</Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Button size="small" variant="outlined" startIcon={<Edit />} onClick={this.openEditModal}>Edit</Button>
          <Button size="small" variant="contained" onClick={this.openCostSimulator}>Cost simulator</Button>
        </Stack>
        <Divider sx={{ mb: 1 }} />
        <TableContainer sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Ingredient</TableCell>
                <TableCell sx={{ width: 120, fontWeight: 600 }} align="right">Qty</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Notes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedForDisplay.map((ln) => (
                <TableRow key={ln.iref}>
                  <TableCell>{this.getProductName(ln.product)}</TableCell>
                  <TableCell align="right">{ln.quantity}</TableCell>
                  <TableCell>{ln.notes}</TableCell>
                </TableRow>
              ))}
              {(!r.recipeItem_recipe || r.recipeItem_recipe.length === 0) && (
                <TableRow><TableCell colSpan={3} align="center">No ingredients</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  }

  renderModal() {
    const { modalOpen, isEdit, form, finishedOptions, ingredientOptions, saving, formErrors } = this.state;

    const onKeyDown = (e) => {
      const isCmdS = (e.metaKey || e.ctrlKey) && (e.key === 's' || e.key === 'S');
      if (isCmdS) { e.preventDefault(); if (!saving) this.onSave(); }
    };

    return (
      <Dialog
        open={modalOpen}
        onClose={() => {}}
        disableEscapeKeyDown
        maxWidth="md"
        fullWidth
        onKeyDown={onKeyDown}
      >
        <DialogTitle>{isEdit ? 'Edit Recipe' : 'New Recipe'}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
            <Autocomplete
              size="small"
              options={finishedOptions}
              value={form.productIref ? finishedOptions.find(o => o.value === form.productIref) || null : null}
              onChange={(_e, opt) => this.setFormField('productIref', opt ? opt.value : '')}
              renderInput={(params) => (
                <TextField {...params} label="Finished product" margin="dense" error={!!formErrors.productIref} helperText={formErrors.productIref || ''} />
              )}
              disabled={isEdit}
            />
            <TextField
              label="Description"
              value={form.description}
              onChange={(e) => this.setFormField('description', e.target.value)}
              size="small"
              margin="dense"
              fullWidth
              required
              error={!!formErrors.description}
              helperText={formErrors.description || ''}
            />
          </Box>

          <Box sx={{ mt: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: .5 }}>
              <Box display="flex" alignItems="center" gap={0.5}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Ingredients</Typography>
                <Typography component="span" color="error">*</Typography>
              </Box>
              {formErrors.ingredients && (
                <Typography color="error" variant="caption" sx={{ mb: 1, display: 'block' }}>
                  {formErrors.ingredients}
                </Typography>
              )}
              <Box sx={{ flexGrow: 1 }} />
              <Button size="small" startIcon={<Add />} onClick={this.addIngredientRow}>Add row</Button>
            </Stack>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 320, fontWeight: 600 }}>Product</TableCell>
                  <TableCell sx={{ width: 100, fontWeight: 600 }} align="right">Qty</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Notes</TableCell>
                  <TableCell sx={{ width: 56 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {(form.ingredients || []).map((ln, idx) => {
                  const rowErr = formErrors.rows?.[idx] || {};
                  return (
                    <TableRow key={idx}>
                      <TableCell>
                        <Autocomplete
                          size="small"
                          options={ingredientOptions}
                          value={ln.productIref ? ingredientOptions.find(o => o.value === ln.productIref) || null : null}
                          onChange={(_e, opt) => this.updateIngredient(idx, { productIref: opt ? opt.value : '' })}
                          renderInput={(params) => (
                            <TextField {...params} label="Ingredient product" margin="dense" error={!!rowErr.product} helperText={rowErr.product || ''} />
                          )}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <TextField
                          type="number"
                          size="small"
                          margin="dense"
                          value={ln.quantity}
                          onChange={(e) => this.updateIngredient(idx, { quantity: Number(e.target.value) })}
                          sx={{ maxWidth: 100 }}
                          inputProps={{ min: 0, step: 1, inputMode: 'numeric', pattern: '[0-9]*' }}
                          onWheel={(e) => e.currentTarget.blur()}
                          error={!!rowErr.quantity}
                          helperText={rowErr.quantity || ''}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField size="small" margin="dense" value={ln.notes} onChange={(e) => this.updateIngredient(idx, { notes: e.target.value })} fullWidth />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Remove row">
                          <span>
                            <IconButton size="small" onClick={() => this.removeIngredientRow(idx)} disabled={(form.ingredients || []).length <= 1}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            {formErrors.ingredients && (
              <Typography variant="caption" color="error" sx={{ mt: .5, display: 'block' }}>
                {formErrors.ingredients}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={this.closeModal}>Cancel</Button>
          <Button variant="contained" onClick={this.onSave} disabled={saving || (Object.keys(formErrors || {}).length > 0)}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // ---- Cost Simulator modal ----
  renderCostSimulatorModal() {
    const {
      simOpen, simMonth, simLoading, simError,
      simWarning, simProductName, simFinishedIref,
      simSourceLabel, simSourceRecipe, simRecipe, simSnapshot,
      finishedOptions, ingredientOptions
    } = this.state;

    const { lines, total } = this.computeCost();
    const priceWithVat = this.numberOr(this.state.simPrice, NaN);
    const vatPct = this.numberOr(this.state.simVat, NaN);

    let priceNoVat = NaN;
    let margin = NaN;

    if (Number.isFinite(priceWithVat)) {
      const factor = Number.isFinite(vatPct) ? (1 + vatPct / 100) : 1;
      priceNoVat = priceWithVat / factor;
      margin = priceNoVat - total;
    }
    const ingredientOptsFiltered = (ingredientOptions || []).filter(o => o.value !== simFinishedIref);

    return (
      <Dialog
        open={simOpen}
        onClose={() => {
          // do nothing on backdrop / esc
        }}
        disableEscapeKeyDown
        fullWidth
        maxWidth="xl"
        PaperProps={{
          sx: {
            width: '90%',
            height: '90vh',
            margin: 'auto',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          },
        }}
      >
        {/* Internal scroller */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {/* Sticky area: title + options + product select */}
          <Box sx={{ position: 'sticky', top: 0, zIndex: 3, bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}>
            <AppBarLike
              title={`Cost simulator — ${simProductName || simFinishedIref || 'Select finished product'}`}
              onClose={this.closeCostSimulator}
              onCsv={this.simExportCsv}
              onPdf={this.simExportPdf}
            />
            <Box sx={{ p: 2, maxWidth: 1200, mx: 'auto' }}>
              {/* Option buttons */}
              <Paper sx={{ p: 2, mb: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Button size="small" variant="outlined" onClick={this.simStartEmpty}>Start empty</Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      const src = this.normalizeRecipeLines(this.state.selected || {});
                      this.setState({
                        simSourceLabel: src.length ? 'current' : 'empty',
                        simSourceRecipe: src,
                        simRecipe: src
                      });
                    }}
                  >
                    Use current recipe
                  </Button>
                  <Tooltip title="Reset working copy to the selected source">
                    <span>
                      <Button size="small" startIcon={<RestartAlt />} onClick={this.simResetToSource} disabled={simSourceRecipe.length === 0}>
                        Reset to source
                      </Button>
                    </span>
                  </Tooltip>
                  <Tooltip title="Clear all rows">
                    <Button size="small" color="warning" startIcon={<CleaningServices />} onClick={this.simClearRows}>
                      Clear
                    </Button>
                  </Tooltip>
                  <Box sx={{ flexGrow: 1 }} />
                  <Chip size="small" label={`source: ${simSourceLabel}`} />
                </Stack>
              </Paper>

              {/* Product select + month + fetch snapshot */}
              <Paper sx={{ p: 2, mb: 0 }}>
                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                  <Autocomplete
                    size="small"
                    options={finishedOptions || []}
                    value={simFinishedIref ? (finishedOptions || []).find(o => o.value === simFinishedIref) || null : null}
                    onChange={(_e, opt) => {
                      const finishedIref = opt ? opt.value : '';
                      const finishedName = opt ? (opt.label || opt.value) : '';

                      const foundRecipe = (this.state.recipes || []).find(
                        r => String(r.product?.iref) === String(finishedIref)
                      );
                      const src = foundRecipe ? this.normalizeRecipeLines(foundRecipe) : [];

                      this.setState({
                        simFinishedIref: finishedIref,
                        simProductName: finishedName,
                        simSourceLabel: src.length ? 'current' : 'empty',
                        simSourceRecipe: src,
                        simRecipe: src,
                        selected: foundRecipe || this.state.selected,
                        simNewProduct: '', 
                      });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Finished product (required)"
                        placeholder="Search finished product…"
                        required
                        error={!simFinishedIref}
                        // helperText={!simFinishedIref ? 'Select a finished product' : ''}
                      />
                    )}
                    sx={{ minWidth: 320 }}
                  />

                  <TextField
                    label="Month"
                    type="month"
                    value={simMonth}
                    onChange={(e) => this.setState({ simMonth: e.target.value })}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                  <Button
                    variant="contained"
                    onClick={this.simFetchSnapshot}
                    disabled={simLoading || this.state.simFetching}
                    size="small"
                  >
                    {simLoading ? 'Fetching…' : 'Fetch prices'}
                  </Button>
                  <TextField
                    label="Price with VAT"
                    type="number"
                    size="small"
                    value={this.state.simPrice}
                    onChange={(e) => this.setState({ simPrice: e.target.value })}
                    inputProps={{ min: '0', step: '0.01' }}
                    sx={{ width: 140 }}
                  />
                  <TextField
                    label="VAT %"
                    type="number"
                    size="small"
                    value={this.state.simVat}
                    onChange={(e) => this.setState({ simVat: e.target.value })}
                    inputProps={{ min: '0', step: '0.1' }}
                    sx={{ width: 80 }}
                  />
                  {!!Object.keys(simSnapshot).length && (
                    <Chip size="small" label={`snapshot: ${Object.keys(simSnapshot).length} prices`} />
                  )}
                  {!!simError && <Typography variant="body2" color="error">{simError}</Typography>}
                  {!!simWarning && (
                  <Typography variant="body2" color="warning.main">
                    {simWarning}
                  </Typography>
                )}
                </Stack>
              </Paper>
            </Box>
          </Box>

          {/* Scrollable body */}
          <Box sx={{ p: 2, maxWidth: 1200, mx: 'auto' }}>
            {/* Recipe editor */}
            <Paper sx={{ p: 2, mb: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>Recipe</Typography>
                <Box sx={{ flexGrow: 1 }} />
                <Button size="small" onClick={this.simAddRow}>Add row</Button>
              </Stack>

              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 360, fontWeight: 600 }}>Ingredient</TableCell>
                    <TableCell sx={{ width: 120, fontWeight: 600 }} align="right">Qty</TableCell>
                    <TableCell sx={{ width: 120, fontWeight: 600 }} align="center">Snapshot?</TableCell>
                    <TableCell sx={{ width: 160, fontWeight: 600 }} align="right">Unit price</TableCell>
                    <TableCell sx={{ width: 56 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {simRecipe.map((row, idx) => {
                    const selectedOpt = row.iref ? ingredientOptsFiltered.find(o => o.value === row.iref) || null : null;
                    const unitFromSnap = row.useSnapshot && Number.isFinite(this.state.simSnapshot[row.iref]) ? this.state.simSnapshot[row.iref] : null;

                    return (
                      <TableRow key={idx}>
                        <TableCell>
                          <Autocomplete
                            size="small"
                            options={ingredientOptsFiltered}
                            value={selectedOpt}
                            onChange={(_e, opt) =>
                              this.simUpdateRow(idx, {
                                iref: opt ? opt.value : '',
                                name: opt ? (opt.label || opt.value) : ''
                              })
                            }
                            renderInput={(params) => <TextField {...params} label="Ingredient" margin="dense" />}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <TextField
                            value={row.qty}
                            onChange={(e) => this.simUpdateRow(idx, { qty: Number(e.target.value) })}
                            type="number"
                            size="small"
                            margin="dense"
                            inputProps={{ step: '0.001', min: '0' }}
                            sx={{ maxWidth: 120 }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Checkbox
                            checked={!!row.useSnapshot}
                            onChange={(e) => this.simUpdateRow(idx, { useSnapshot: e.target.checked })}
                            inputProps={{ 'aria-label': 'use snapshot' }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <TextField
                            value={row.price}
                            onChange={(e) => this.simUpdateRow(idx, { price: e.target.value })}
                            type="number"
                            size="small"
                            inputProps={{ step: '0.0001', min: '0' }}
                            disabled={!!row.useSnapshot}
                            placeholder={row.useSnapshot ? (unitFromSnap != null ? String(unitFromSnap) : 'from snapshot') : 'e.g. 1.2345'}
                            sx={{ maxWidth: 140 }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Remove row">
                            <IconButton size="small" onClick={() => this.simRemoveRow(idx)}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {simRecipe.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ color: 'text.secondary' }}>
                        No ingredients. Start empty or load current recipe.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Paper>

            {/* Cost breakdown */}
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>Cost Breakdown</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Ingredient</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell align="right">Unit price</TableCell>
                    <TableCell align="right">Line cost</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lines.map(l => (
                    <TableRow key={l.iref}>
                      <TableCell>{l.name}</TableCell>
                      <TableCell align="right">{this.numberOr(l.qty).toFixed(3)}</TableCell>
                      <TableCell align="right">{Number.isFinite(l.unitPrice) ? l.unitPrice.toFixed(4) : <em>n/a</em>}</TableCell>
                      <TableCell align="right">{Number.isFinite(l.lineCost) ? l.lineCost.toFixed(4) : <em>n/a</em>}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow><TableCell colSpan={4}><Divider sx={{ my: 1 }} /></TableCell></TableRow>
                  <TableRow>
                    <TableCell colSpan={3} align="right"><strong>Total cost</strong></TableCell>
                    <TableCell align="right"><strong>{total.toFixed(4)}</strong></TableCell>
                  </TableRow>
                  {Number.isFinite(priceWithVat) && (
                    <>
                      <TableRow>
                        <TableCell colSpan={3} align="right">Price with VAT</TableCell>
                        <TableCell align="right">{priceWithVat.toFixed(2)}</TableCell>
                      </TableRow>
                      {/* {Number.isFinite(priceNoVat) && (
                        <TableRow>
                          <TableCell colSpan={3} align="right">Price without VAT</TableCell>
                          <TableCell align="right">{priceNoVat.toFixed(4)}</TableCell>
                        </TableRow>
                      )} */}
                      {Number.isFinite(margin) && (
                        <TableRow>
                          <TableCell colSpan={3} align="right">
                            <strong>Margin </strong>
                          </TableCell>
                          <TableCell align="right">
                            <strong
                              style={{ color: margin >= 0 ? '#2e7d32' : '#c62828' }}
                            >
                              {margin.toFixed(4)}
                            </strong>
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  )}
                </TableBody>
              </Table>

              <Box
                sx={{
                  pt: 2,
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 1,
                }}
              >
                <TextField
                  label="New product"
                  size="small"
                  value={this.state.simNewProduct}
                  onChange={(e) => this.setState({ simNewProduct: e.target.value })}
                  sx={{ minWidth: 240, flexGrow: 1 }}
                />
                <Box sx={{ flexGrow: 1 }} />
                <Button variant="outlined" onClick={this.closeCostSimulator}>Cancel</Button>
                <Button variant="outlined" onClick={this.simExportCsv} disabled={!this.canExport()}>
                  Export CSV
                </Button>
                <Button
                  variant="contained"
                  onClick={this.simExportPdf}
                  disabled={!this.canExport() || this.state.simExporting}
                >
                  {this.state.simExporting ? 'Exporting…' : 'Export PDF'}
                </Button>
              </Box>
            </Paper>
          </Box>
        </Box>
      </Dialog>
    );
  }

  render() {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          minHeight: 0,
          maxHeight: '100%',
          minWidth: 0,
          overflow: 'hidden',
          bgcolor: 'background.default',
          p: {
            xs: 0.5,
            sm: 0.85,
            md: 1.1,
          },
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {this.renderToolbar()}

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            overflow: 'hidden',
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: 'minmax(0, 1fr) minmax(0, 1fr)',
            },
            gap: 1,
          }}
        >
          <Box
            sx={{
              minWidth: 0,
              minHeight: 0,
              width: '100%',
              height: '100%',
              overflow: 'hidden',
            }}
          >
            {this.renderList()}
          </Box>

          <Box
            sx={{
              minWidth: 0,
              minHeight: 0,
              width: '100%',
              height: '100%',
              overflow: 'hidden',
            }}
          >
            {this.renderDetails()}
          </Box>
        </Box>

        <Dialog open={this.state.errorDialog.open} onClose={this.closeError} maxWidth="sm" fullWidth>
          <DialogTitle>Error</DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{this.state.errorDialog.detail}</Typography>
          </DialogContent>
          <DialogActions><Button onClick={this.closeError}>OK</Button></DialogActions>
        </Dialog>

        <Snackbar open={this.state.toastOpen} autoHideDuration={2500} onClose={this.closeToast} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
          <Alert onClose={this.closeToast} severity={this.state.toastSeverity} variant="filled" sx={{ width: '100%' }}>
            {this.state.toastMsg}
          </Alert>
        </Snackbar>

        {this.renderModal()}
        {this.renderCostSimulatorModal()}
      </Box>
    );
  }
}

export default ProductRecipes;
